import {
  ColDef,
  GetContextMenuItemsParams,
  MenuItemDef,
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { useMountEffect } from 'primereact/hooks';
import { InputText } from 'primereact/inputtext';
import { TabPanel, TabView } from 'primereact/tabview';
import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FundDocument } from '~/api/def/endpoints/fundEndpoint';
import { FundFormDialog } from '~/components/forms/FundFormDialog';
import {
  Breadcrumb,
  BreadcrumbProps,
} from '~/components/ui/layout/navigation/breadcrumb/Breadcrumb';
import { agTheme } from '~/components/ui/tables/ag/agTheme';
import { colTypesWidths } from '~/components/ui/tables/ag/columnTypes';
import { getColDefsDocumentTable } from '~/components/ui/tables/ag/documentsTable/colDefsDocumentTable';
import { DocumentFilterPanel } from '~/components/ui/tables/ag/documentsTable/DocumentFilterPanel';
import { getDocumentsContextMenu } from '~/components/ui/tables/ag/documentsTable/getDocumentsContextMenu';
import { GoogleDriveUploader } from '~/components/ui/uploaders/GoogleDriveUploader';
import { getFundByIdFromHierarchy } from '~/lib/fund/fundsToHierarchy';
import { getHierarchyPath } from '~/lib/fund/getHierarchyPath';
import { useFilterTable } from '~/lib/table/useFilterTable';
import { Fund, Hierarchy } from '~/types';

import { Provider, useAtom } from 'jotai';
import {
  documentTableLoading,
  fundIdAtom,
} from '~/atoms/document/documentAtoms';
import { DocumentOverviewStore } from '~/atoms/document/documentOverviewAtoms';
import { DialogDocumentOverview } from '~/components/ui/document/dialogs/documentOverviewDialog/DialogDocumentOverview';
import { DocumentCompareContainer } from '~/components/ui/document/DocumentCompareContainer';
import { HeaderContainer } from '~/components/ui/layout/page/HeaderContainer';
import { PageContent } from '~/components/ui/layout/page/PageContent';
import { PageTagline } from '~/components/ui/layout/page/PageTagline';
import { useDocumentLoader } from '~/components/ui/tables/ag/documentsTable/useDocumentLoader';
import { useDocumentsUrlParams } from '~/components/ui/tables/ag/documentsTable/useDocumentsUrlParams';
import { DocumentUploaderQueue } from '~/components/ui/uploaders/DocumentUploaderQueue';
import { EditDocumentForm } from './EditDocumentForm';
import { OptionCell } from './OptionCell';
import { useHierarchyEndpoint } from '~/api/useHierarchyEndpoint';

export type DocumentTableProps = {
  hierarchy?: Hierarchy[] | null;
  fundId?: string;
};

export const TableAgDocuments = ({ hierarchy, fundId }: DocumentTableProps) => {
  const { loadHierarchy } = useHierarchyEndpoint();
  const [loadingDocuments] = useAtom(documentTableLoading);
  const [colDefs, setColDefs] = useState<ColDef<FundDocument>[]>([]);
  const [agreementTypes, setAgreementTypes] = useState<FundDocument['type'][]>(
    [],
  );
  const { loadDocuments, documents } = useDocumentLoader();
  const gridRef = useRef<AgGridReact<FundDocument> | null>(null);
  const {
    filteredData,
    setExternalFilters,
    externalFilters,
    setFundNameSearch,
    doesExternalFilterPass,
    fundNameSearch,
  } = useFilterTable(documents ?? []);
  const [breadcrumbs, setBreadcrumbs] = useState<BreadcrumbProps | undefined>();
  const [fundName, setFundName] = useState<string | undefined>();

  const [isUploadDialogVisible, setIsUploadDialogVisible] =
    useState<boolean>(false);
  const [isEditFundDialogVisible, setIsEditFundDialogVisible] =
    useState<boolean>(false);
  const {
    isEditDocumentDialogVisible,
    isDocumentCompareDialogVisible,
    showEditDocumentDialog,
    showDocumentOverview,
    hideDocumentCompare,
    hideEditDocumentDialog,
    showDocumentsCompare,
  } = useDocumentsUrlParams();
  const [activeIndex, setActiveIndex] = useState(0);

  const [, setAtomFundId] = useAtom(fundIdAtom);
  const fund = useMemo((): Fund | undefined | null => {
    if (hierarchy && fundId) {
      return getFundByIdFromHierarchy(fundId, hierarchy);
    }
  }, [fundId, hierarchy]);
  useEffect(() => {
    if (fundId) {
      setAtomFundId(fundId);
    }
    return () => {
      setAtomFundId(null);
    };
  }, [fundId]);

  useEffect(() => {
    const colDefs = getColDefsDocumentTable({ showDocumentOverview });
    colDefs.push(
      {
        field: 'portfolioCompanyName',
        headerName: 'Portfolio Company Name',
        filter: 'agDateColumnFilter',
        cellClass: 'flex align-items-center',
        sortable: true,
        resizable: true,
        flex: 1,
        valueFormatter: (p) => p.value || '-',
      },
      {
        field: 'options',
        headerName: '',
        filter: 'agDateColumnFilter',
        cellClass: 'flex align-items-center',
        sortable: true,
        resizable: true,
        type: 'w120',
        cellRenderer: (p: { data: FundDocument }) =>
          OptionCell({
            ...p,
            showEditDocumentDialog: showEditDocumentDialog,
          }),
      },
    );
    setColDefs(colDefs);
  }, []);

  useEffect(() => {
    if (hierarchy && fundId) {
      const path = getHierarchyPath({
        hierarchy: hierarchy,
        level: 'fund',
        fundId,
      });
      setFundName(path?.fund?.name);
      setBreadcrumbs({
        items: [
          { name: 'Funds', url: '/documents' },
          // {name: path?.jurisdiction?.name??'not found'} ,
          { name: path?.fund?.name ?? 'not found' },
        ],
      });
    }
  }, [hierarchy, fundId]);

  const ctxDocumentsContextMenu = (
    params: GetContextMenuItemsParams<FundDocument>,
  ): (string | MenuItemDef)[] => {
    return getDocumentsContextMenu(params, {
      showDocumentOverview,
      loadDocuments,
    });
  };

  /** trigger table filter when external filter on fundName changes
   * !IMPORTANT: api.onFilterChanged() has to be called to trigger the filter
   * */
  const externalFilterChanged = useCallback((newValue: string) => {
    setFundNameSearch(newValue);
    gridRef.current?.api.onFilterChanged();
  }, []);

  /** Set default state of the status filters
   * */
  useMountEffect(() => {
    setExternalFilters({ status: 'Live' });
  });

  /** Collect all unique agreement types from the documents
   *  TODO remove this logic fom component body
   * */
  useEffect(() => {
    if (documents) {
      const agreementTypes = documents.reduce(
        (acc: FundDocument['type'][], doc) => {
          if (!acc.includes(doc.type)) {
            acc.push(doc.type);
          }
          return acc;
        },
        [],
      );
      setAgreementTypes(agreementTypes);
    }
  }, [documents]);

  const [selectedCount, setSelectedCount] = useState(0);
  const [selectedDocuments, setSelectedDocuments] = useState<FundDocument[]>(
    [],
  );

  const onSelectionChanged = useCallback(() => {
    const selectedNodes = gridRef.current?.api.getSelectedNodes() || [];
    setSelectedDocuments(
      selectedNodes
        .map((node) => node.data)
        .filter((doc): doc is FundDocument => doc !== undefined),
    );
    setSelectedCount(selectedNodes.length);
  }, []);

  const isRowSelectable = useCallback(
    (node: { isSelected: () => boolean }) => {
      return selectedCount < 2 || node.isSelected();
    },
    [selectedCount],
  );

  return (
    <div className="flex-column flex h-full">
      <Provider store={DocumentOverviewStore}>
        <DialogDocumentOverview fundName={fundName} />
      </Provider>
      <Dialog
        onHide={() => {
          setSelectedDocuments([]);
          hideDocumentCompare();
        }}
        header="Document Compare"
        style={{ width: '100vw', height: '100vh', maxHeight: '100vh' }}
        visible={isDocumentCompareDialogVisible}
        pt={{ content: { style: { height: '100%' } } }}
      >
        <DocumentCompareContainer
          visible={isDocumentCompareDialogVisible}
          fundName={fundName || ''}
          userSelectedDocuments={selectedDocuments}
        />
      </Dialog>
      <Dialog
        header="Edit Document"
        visible={isEditDocumentDialogVisible}
        onHide={() => hideEditDocumentDialog()}
        pt={{
          header: {
            style: {
              padding: '1rem',
            },
          },
          content: {
            style: {
              width: '24rem',
              height: '20rem',
              padding: '0rem',
            },
          },
        }}
      >
        <EditDocumentForm
          onHide={() => hideEditDocumentDialog()}
          onSubmitComplete={() => loadDocuments(fundId)}
          fundId={fundId || ''}
        />
      </Dialog>
      <Dialog
        onHide={() => setIsUploadDialogVisible(false)}
        header="Upload Document"
        style={{ width: '80vw', minHeight: '36rem' }}
        visible={isUploadDialogVisible}
      >
        <div className="p-m-5">
          <TabView
            activeIndex={activeIndex}
            onTabChange={(e) => setActiveIndex(e.index)}
          >
            <TabPanel header="Local Machine">
              <DocumentUploaderQueue
                fundId={fundId || ''}
                onSubmitComplete={() => loadDocuments(fundId)}
              />
            </TabPanel>
            <TabPanel header="Google Drive">
              <GoogleDriveUploader
                onSubmitComplete={() => loadDocuments(fundId)}
                onUploadInit={() => setIsUploadDialogVisible(false)}
                fundId={fundId || ''}
                showPcFolderMapping
                isPcFolderMapped={fund?.pcGDriveFolderSync}
                refetchFund={() => loadHierarchy()}
              />
            </TabPanel>
          </TabView>
        </div>
      </Dialog>
      {fund && (
        <FundFormDialog
          formMode="edit"
          onHide={() => setIsEditFundDialogVisible(false)}
          visible={isEditFundDialogVisible}
          fundId={fund.fundId}
          initialValue={fund}
        />
      )}

      <HeaderContainer>
        <Breadcrumb items={breadcrumbs?.items} />
        <PageTagline message="List of funds grouped by jurisdiction" />
      </HeaderContainer>
      <PageContent>
        <DocumentFilterPanel
          agreementTypes={agreementTypes}
          filters={externalFilters}
          setFilter={setExternalFilters}
        />

        <div className="document_search border-gray-300 pt-2 mt-2 pb-2 flex align-items-center justify-content-left border-top-1">
          <h2 className="pr-2 pb-0 pt-2">Fund Documents</h2>
          <InputText
            value={fundNameSearch}
            className="search-input"
            style={{
              backgroundColor: 'var(--bq-color-gray-lighter)',
            }}
            onChange={(e) => externalFilterChanged(e.target.value)}
            placeholder="Search by Fund Name"
          />
          <div className="buttons">
            <Button
              label="Upload "
              onClick={() => setIsUploadDialogVisible(true)}
              icon="pi pi-plus"
            />
            {fund && (
              <Button
                label="Edit Fund "
                onClick={() => setIsEditFundDialogVisible(true)}
                icon="pi pi-plus"
                className="ml-4"
              />
            )}
            <Button
              label="Compare document"
              disabled={selectedDocuments.length < 2}
              onClick={async () => showDocumentsCompare()}
              icon="pi pi-align-left"
              className="ml-4"
            />
          </div>
        </div>
        <div
          className={'ag-theme-quartz'}
          style={{ flex: '1 1 0px', width: '100%', height: '75vh' }}
        >
          <AgGridReact<FundDocument>
            rowData={filteredData}
            ref={gridRef}
            loading={loadingDocuments}
            suppressCellFocus
            getRowId={(data) => data.data.id}
            isExternalFilterPresent={() => fundNameSearch !== ''}
            doesExternalFilterPass={doesExternalFilterPass}
            // getMainMenuItems={getMainMenuItems}
            onGridSizeChanged={() => gridRef.current?.api.sizeColumnsToFit()}
            getContextMenuItems={ctxDocumentsContextMenu}
            columnDefs={colDefs}
            columnTypes={colTypesWidths}
            tooltipShowDelay={0}
            theme={agTheme}
            autoSizeStrategy={{
              type: 'fitGridWidth',
              defaultMinWidth: 100,
            }}
            rowSelection={{
              mode: 'multiRow',
              isRowSelectable: isRowSelectable,
              headerCheckbox: false,
            }}
            onSelectionChanged={onSelectionChanged}
          />
        </div>
      </PageContent>
    </div>
  );
};
